import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Input, SelectItem } from "@heroui/react";
import AppSelect from "../../components/common/appSelect";
import React from "react";
import { useTranslation } from "react-i18next";
import { FiMessageCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import noFeedsEmptyIcon from "../../assets/icons/noFeedsEmpty.svg";
import { CreatePostSection } from "../../components/dashboard/createPostSection";
import FeedsModals from "../../components/feeds/feedsModals";
import PostCard from "../../components/feeds/postCard";
import {
  CommentResponseDto,
  CommonProfileDto,
  EditingPost,
  PostResponseDto,
} from "../../types/types";
import {
  formatEnumString,
  formatSportName,
  formatWeightClassName,
} from "../../utils/utilMethods";
import { FeedsFilters } from "./feedsFilters";

interface FeedsFilterProps {
  activeTab: "all" | "my";
  handleTabChange: (key: React.Key) => void;
  t: ReturnType<typeof useTranslation<"feeds">>["t"];
  filterProps: any;
}

const mobileSelectClassNames = {
  base: "w-full",
  trigger:
    "h-10 min-h-10 rounded-xl bg-white border border-gray-300 shadow-none pl-3 pr-0 data-[hover=true]:bg-gray-50",
  value: "text-sm font-normal leading-5 text-gray-500 group-data-[has-value=true]:text-gray-900",
  selectorIcon: "text-gray-500 w-4 h-4 mr-3",
  popoverContent: "rounded-xl font-['Plus_Jakarta_Sans']",
};

export const FeedsFilter: React.FC<FeedsFilterProps> = ({
  activeTab,
  handleTabChange,
  t,
  filterProps,
}) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const managerTypeOptions = ["INDIVIDUAL", "AGENCY"];

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filterProps.selectedSport) count++;
    if (filterProps.selectedWeight) count++;
    if (filterProps.selectedManagerType) count++;
    if (filterProps.selectedProfileType) count++;
    return count;
  }, [
    filterProps.selectedSport,
    filterProps.selectedWeight,
    filterProps.selectedManagerType,
    filterProps.selectedProfileType,
  ]);

  React.useEffect(() => {
    if (isFilterDrawerOpen) {
      filterProps.loadSportsIfNeeded();
      filterProps.loadWeightClassesIfNeeded();
    }
  }, [isFilterDrawerOpen, filterProps]);

  return (
    <>
      <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-gray-100 flex items-center justify-between gap-2 px-4 py-3 font-['Plus_Jakarta_Sans']">
        <div className="flex items-center bg-gray-100 p-1 rounded-full shrink-0">
          <button
            type="button"
            onClick={() => handleTabChange("all")}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              activeTab === "all"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t("allPosts", "All Posts")}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("my")}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              activeTab === "my"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t("myPosts", "My Posts")}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
          {isSearchOpen && (
            <div className="flex-1 min-w-0">
              <Input
                autoFocus
                aria-label={t("search_by_title", "Search by title")}
                placeholder={t("search_by_title", "Search by title")}
                value={filterProps.searchQuery}
                onChange={(e) => filterProps.setSearchQuery(e.target.value)}
                startContent={
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                }
                size="sm"
                radius="full"
                className="w-full"
                classNames={{
                  inputWrapper:
                    "h-9 bg-white border border-gray-200 shadow-none rounded-full",
                  input:
                    "text-xs sm:text-sm font-normal leading-5 placeholder:text-gray-400",
                }}
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsSearchOpen((prev) => !prev)}
            className={`size-9 min-w-9 rounded-2xl border transition-colors flex items-center justify-center shrink-0 shadow-sm ${
              isSearchOpen
                ? "bg-blue-50 border-blue-200 text-blue-600"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
          </button>

          {activeTab === "all" && (
            <button
              type="button"
              onClick={() => {
                filterProps.loadSportsIfNeeded();
                filterProps.loadWeightClassesIfNeeded();
                setIsFilterDrawerOpen(true);
              }}
              className="size-9 min-w-9 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors shadow-sm relative shrink-0"
              aria-label="Filter"
            >
              <FunnelIcon className="w-4 h-4 text-gray-700" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 size-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {isFilterDrawerOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
            onClick={() => setIsFilterDrawerOpen(false)}
          />
          <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl flex flex-col gap-4 z-10 font-['Plus_Jakarta_Sans']">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">
                  {t("filters", { defaultValue: "Filters" })}
                </h2>
                {activeFilterCount > 0 && (
                  <span className="size-5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="size-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                aria-label="Close filters"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col gap-3">
              <AppSelect
                aria-label={t("select_sport", "Select sport")}
                placeholder={t("select_sport", "Select sport")}
                selectedKeys={
                  filterProps.selectedSport ? [filterProps.selectedSport] : []
                }
                onSelectionChange={(key) =>
                  filterProps.setSelectedSport(
                    (Array.from(key)[0] as string) || ""
                  )
                }
                onOpenChange={(isOpen) =>
                  isOpen && filterProps.loadSportsIfNeeded()
                }
                classNames={mobileSelectClassNames}
                size="sm"
              >
                {filterProps.sports.length > 0 ? (
                  filterProps.sports.map((sport: string) => (
                    <SelectItem key={sport}>
                      {formatSportName(sport)}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem key="no-sport" isDisabled>
                    {t("no_sports_available", "No sports available")}
                  </SelectItem>
                )}
              </AppSelect>

              <AppSelect
                aria-label={t("select_weight_class", "Select weight")}
                placeholder={t("select_weight_class", "Select weight")}
                selectedKeys={
                  filterProps.selectedWeight
                    ? [filterProps.selectedWeight]
                    : []
                }
                onSelectionChange={(key) =>
                  filterProps.setSelectedWeight(
                    (Array.from(key)[0] as string) || ""
                  )
                }
                onOpenChange={(isOpen) =>
                  isOpen && filterProps.loadWeightClassesIfNeeded()
                }
                classNames={mobileSelectClassNames}
                size="sm"
              >
                {filterProps.weightClasses.length > 0 ? (
                  filterProps.weightClasses.map((weight: string) => (
                    <SelectItem key={weight}>
                      {formatWeightClassName(weight)}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem key="no-weight" isDisabled>
                    {t(
                      "no_weight_classes_available",
                      "No weight classes available"
                    )}
                  </SelectItem>
                )}
              </AppSelect>

              <AppSelect
                aria-label={t("select_manager_type", "Select manager")}
                placeholder={t("select_manager_type", "Select manager")}
                selectedKeys={
                  filterProps.selectedManagerType
                    ? [filterProps.selectedManagerType]
                    : []
                }
                onSelectionChange={(key) =>
                  filterProps.setSelectedManagerType(
                    (Array.from(key)[0] as string) || ""
                  )
                }
                classNames={mobileSelectClassNames}
                size="sm"
              >
                {managerTypeOptions.map((type) => (
                  <SelectItem key={type}>
                    {formatEnumString(type)}
                  </SelectItem>
                ))}
              </AppSelect>
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  filterProps.resetFilters();
                  setIsFilterDrawerOpen(false);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-center"
              >
                {t("reset_filters", { defaultValue: "Reset all" })}
              </button>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 transition-colors text-center shadow-sm"
              >
                {t("apply_filters", { defaultValue: "Apply Filters" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="hidden lg:flex sticky top-0 z-20 bg-white border-b border-gray-100 flex-shrink-0 w-full px-6 py-4 items-center justify-between font-['Plus_Jakarta_Sans']">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] shrink-0">Feeds</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="px-1.5 py-1 bg-gray-100 rounded-3xl flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => handleTabChange("all")}
              style={{ border: "none", outline: "none" }}
              className="relative px-3 py-1.5 rounded-3xl flex items-center transition-all duration-150 cursor-pointer"
            >
              {activeTab === "all" && (
                <span className="absolute inset-0 rounded-3xl bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]" />
              )}
              <span
                className={`relative text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 whitespace-nowrap ${
                  activeTab === "all" ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {t("allPosts", "All Posts")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("my")}
              style={{ border: "none", outline: "none" }}
              className="relative px-3 py-1.5 rounded-3xl flex items-center transition-all duration-150 cursor-pointer"
            >
              {activeTab === "my" && (
                <span className="absolute inset-0 rounded-3xl bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]" />
              )}
              <span
                className={`relative text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 whitespace-nowrap ${
                  activeTab === "my" ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {t("myPosts", "My Posts")}
              </span>
            </button>
          </div>

          <div className="h-6 w-px bg-gray-200 shrink-0" />

          <FeedsFilters {...filterProps} />
        </div>
      </div>
    </>
  );
};

interface FeedsContentProps {
  loading: boolean;
  posts: PostResponseDto[];
  activeTab: "all" | "my";
  currentUserId: string | number;
  topProfiles: CommonProfileDto[];
  likedPosts: { [postId: number]: boolean };
  comments: { [postId: number]: CommentResponseDto[] };
  newComment: { [postId: number]: string };
  expandedComments: { [postId: number]: boolean };
  currentMediaIndex: { [postId: number]: number };
  editingPost: EditingPost | null;
  showOptionsMenu: number | null;
  showLikesModal: number | null;
  loadingLikes: boolean;
  likesData: any[];
  deleteModalPostId: number | null;
  reportModalPostId: number | null;
  reportReason: string;
  t: ReturnType<typeof useTranslation<"feeds">>["t"];
  onLike: (postId: number, isLiked: boolean) => Promise<void>;
  onShowLikes: (postId: number) => Promise<void>;
  onCloseLikesModal: () => void;
  onEdit: (post: PostResponseDto) => void;
  onDelete: () => void;
  onSavePost: (postId: number) => Promise<void>;
  onAddComment: (e: React.FormEvent, postId: number) => Promise<void>;
  onDeleteComment: (postId: number, commentId: number) => Promise<void>;
  onToggleComments: (postId: number) => Promise<void>;
  onSetNewComment: React.Dispatch<React.SetStateAction<{ [postId: number]: string }>>;
  onSetShowOptionsMenu: React.Dispatch<React.SetStateAction<number | null>>;
  onSetEditingPost: React.Dispatch<React.SetStateAction<EditingPost | null>>;
  onRemoveSingleMedia: (index: number) => void;
  onSetCurrentMediaIndex: React.Dispatch<React.SetStateAction<{ [postId: number]: number }>>;
  onCloseDeleteModal: () => void;
  onConfirmDelete: (postId: number) => Promise<void>;
  onOpenReportModal: (postId: number) => void;
  onCloseReportModal: () => void;
  onSetReportReason: React.Dispatch<React.SetStateAction<string>>;
  onConfirmReport: (postId: number) => Promise<void>;
}

export const FeedsContent: React.FC<FeedsContentProps> = ({
  loading,
  posts,
  activeTab,
  topProfiles,
  currentUserId,
  likedPosts,
  comments,
  newComment,
  expandedComments,
  currentMediaIndex,
  editingPost,
  showOptionsMenu,
  showLikesModal,
  loadingLikes,
  likesData,
  deleteModalPostId,
  reportModalPostId,
  reportReason,
  t,
  onLike,
  onShowLikes,
  onCloseLikesModal,
  onEdit,
  onDelete,
  onSavePost,
  onAddComment,
  onDeleteComment,
  onToggleComments,
  onSetNewComment,
  onSetShowOptionsMenu,
  onSetEditingPost,
  onRemoveSingleMedia,
  onSetCurrentMediaIndex,
  onCloseDeleteModal,
  onConfirmDelete,
  onOpenReportModal,
  onCloseReportModal,
  onSetReportReason,
  onConfirmReport,
}) => {
  const navigate = useNavigate();
  const handleViewProfile = (profileId?: string | number | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  if (loading) {
    return (
      <div className="max-w-[680px] w-full mx-auto px-3 sm:px-4 py-4 sm:py-6 animate-pulse">
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm mb-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
            <div className="space-y-1.5 flex-1">
              <div className="h-4 w-32 bg-slate-200 rounded" />
              <div className="h-3 w-48 bg-slate-100 rounded" />
            </div>
          </div>
          <div className="h-10 bg-slate-100 rounded-xl w-full" />
          <div className="h-20 bg-slate-100 rounded-xl w-full" />
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-100" />
              <div className="w-8 h-8 rounded-full bg-slate-100" />
            </div>
            <div className="h-8 w-20 bg-blue-100 rounded-xl" />
          </div>
        </div>

        {activeTab !== "my" && (
          <div className="mb-8">
            <div className="mb-3 space-y-1">
              <div className="h-4 w-40 bg-slate-200 rounded" />
              <div className="h-3 w-28 bg-slate-100 rounded" />
            </div>
            <div className="flex items-center gap-3 overflow-hidden pb-2 pt-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl p-1.5 border border-slate-100 flex flex-col items-center w-28 sm:w-32 shrink-0 space-y-2 shadow-sm"
                >
                  <div className="w-full h-32 sm:h-36 rounded-2xl bg-slate-200" />
                  <div className="h-3.5 w-20 bg-slate-200 rounded mb-1" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="h-4 w-28 bg-slate-200 rounded mb-3" />
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-4 w-36 bg-slate-200 rounded" />
                  <div className="h-3 w-24 bg-slate-100 rounded" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-slate-200 rounded" />
                <div className="h-4 w-4/5 bg-slate-100 rounded" />
              </div>
              <div className="w-full h-64 rounded-2xl bg-slate-200" />
              <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                <div className="h-6 w-16 bg-slate-100 rounded-lg" />
                <div className="h-6 w-16 bg-slate-100 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[680px] w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
      <div className="mb-6">
        <CreatePostSection />
      </div>

      {activeTab !== "my" && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-bold text-gray-900 tracking-tight">
                {t("mostViewedProfiles", "Most viewed profiles")}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {t("mostViewedProfilesSubtitle", "Top active members")}
              </p>
            </div>
          </div>

          {topProfiles.length === 0 ? (
            <div className="bg-white rounded-3xl p-6 text-center shadow-sm border border-gray-100 text-gray-500">
              <p className="text-sm font-medium">{t("noDataForSelectedFilters")}</p>
              <p className="text-xs mt-1 text-gray-400">{t("noDataSuggestion")}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide pt-1">
                {topProfiles.slice(0, 5).map((profile, index) => (
                  <button
                    type="button"
                    key={profile.id}
                    onClick={() => handleViewProfile(profile.id)}
                    className="bg-white rounded-3xl p-1.5 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)] border border-gray-100/80 flex flex-col items-center w-28 sm:w-32 shrink-0 hover:shadow-md hover:-translate-y-0.5 transition-all text-left outline-none cursor-pointer group"
                    aria-label={`${t("viewProfile", "View profile")} ${profile.firstName} ${profile.lastName}`}
                  >
                    <div className="w-full h-32 sm:h-36 rounded-2xl relative overflow-hidden bg-gray-100">
                      {profile.profileImage ? (
                        <img
                          src={profile.profileImage}
                          alt={`${profile.firstName} ${profile.lastName}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-base">
                          {`${profile.firstName?.[0]?.toUpperCase() || ""}${profile.lastName?.[0]?.toUpperCase() || ""}`}
                        </div>
                      )}
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-amber-100 text-amber-900 border border-amber-200/80 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                          🏆 1st
                        </div>
                      )}
                      {index === 1 && (
                        <div className="absolute top-2 left-2 bg-slate-200 text-slate-800 border border-slate-300/80 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                          🥈 2nd
                        </div>
                      )}
                      {index === 2 && (
                        <div className="absolute top-2 left-2 bg-amber-50 text-amber-900 border border-amber-200/80 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                          🥉 3rd
                        </div>
                      )}
                    </div>
                    <div className="w-full px-1 pt-2 pb-1 text-center">
                      <p className="text-xs font-bold text-gray-900 truncate w-full group-hover:text-blue-600 transition-colors">
                        {profile.firstName} {profile.lastName}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate w-full mt-0.5">
                        {profile.profileType || "Fighter"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              {topProfiles.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  <div className="size-1.5 bg-gray-800 rounded-full" />
                  <div className="size-1.5 bg-gray-300 rounded-full" />
                  <div className="size-1.5 bg-gray-300 rounded-full" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-900 tracking-tight">
          {t("postFeeds", "Post feeds")}
        </h2>
      </div>

      {posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <img
            src={noFeedsEmptyIcon}
            alt="No post"
            className="w-20 h-20 mb-3 object-contain"
          />
          <p className="text-sm font-normal text-gray-500 font-['Plus_Jakarta_Sans']">
            No post
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={currentUserId}
              activeTab={activeTab}
              likedPosts={likedPosts}
              comments={comments}
              newComment={newComment}
              expandedComments={expandedComments}
              currentMediaIndex={currentMediaIndex}
              editingPost={editingPost}
              showOptionsMenu={showOptionsMenu}
              posts={posts}
              onLike={onLike}
              onShowLikes={onShowLikes}
              onEdit={onEdit}
              onDelete={onDelete}
              onSavePost={onSavePost}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              onToggleComments={onToggleComments}
              onSetNewComment={onSetNewComment}
              onSetShowOptionsMenu={onSetShowOptionsMenu}
              onSetEditingPost={onSetEditingPost}
              onRemoveSingleMedia={onRemoveSingleMedia}
              onSetCurrentMediaIndex={onSetCurrentMediaIndex}
              onOpenReportModal={onOpenReportModal}
              onViewProfile={handleViewProfile}
              t={t}
            />
          ))}
        </div>
      )}
      <FeedsModals
        showLikesModal={showLikesModal}
        loadingLikes={loadingLikes}
        likesData={likesData}
        deleteModalPostId={deleteModalPostId}
        reportModalPostId={reportModalPostId}
        reportReason={reportReason}
        onCloseLikesModal={onCloseLikesModal}
        onCloseDeleteModal={onCloseDeleteModal}
        onConfirmDelete={onConfirmDelete}
        onCloseReportModal={onCloseReportModal}
        onSetReportReason={onSetReportReason}
        onConfirmReport={onConfirmReport}
        onViewProfile={handleViewProfile}
        t={t}
      />
    </div>
  );
};
